import { BANKING_SCOPE } from '../types/banking-scopes'
import type { BankingCapabilities, BankingPermissionTier } from '../types/jack-henry-banking'
import {
  DEFAULT_BANKING_CAPABILITIES,
  maxBankingPermissionTier
} from '../types/jack-henry-banking'
import { normalizeProductOAuthSecretScopes } from './normalize-product-oauth-scopes'

function hasAny(scopes: Set<string>, ...keys: string[]): boolean {
  return keys.some((k) => scopes.has(k))
}

function tierFromReadWrite(
  scopes: Set<string>,
  readKey: string,
  writeKey: string
): BankingPermissionTier {
  if (scopes.has(writeKey)) {
    return 'write'
  }
  if (scopes.has(readKey)) {
    return 'read'
  }
  return 'none'
}

/**
 * Reconstructs banking capability tiers from OAuth scope strings stored on a secret.
 */
export function deriveBankingCapabilitiesFromGrantedScopes(
  scopes: readonly string[]
): BankingCapabilities {
  const s = new Set(normalizeProductOAuthSecretScopes(scopes))
  const out: BankingCapabilities = { ...DEFAULT_BANKING_CAPABILITIES }

  out.customers = tierFromReadWrite(s, BANKING_SCOPE.CUSTOMER_READ, BANKING_SCOPE.CUSTOMER_WRITE)
  out.accounts = tierFromReadWrite(s, BANKING_SCOPE.ACCOUNT_READ, BANKING_SCOPE.ACCOUNT_WRITE)
  out.transfers = tierFromReadWrite(
    s,
    BANKING_SCOPE.TRANSFER_READ,
    BANKING_SCOPE.TRANSFER_WRITE
  )
  out.transactions = tierFromReadWrite(
    s,
    BANKING_SCOPE.TRANSACTIONS_READ,
    BANKING_SCOPE.TRANSACTIONS_WRITE
  )
  out.loan = tierFromReadWrite(s, BANKING_SCOPE.LOAN_READ, BANKING_SCOPE.LOAN_WRITE)

  if (hasAny(s, BANKING_SCOPE.RDC_REQUESTS_READ)) {
    out.rdcRequests = 'read'
  }

  out.rdcAccounts = tierFromReadWrite(
    s,
    BANKING_SCOPE.RDC_ACCOUNTS_READ,
    BANKING_SCOPE.RDC_ACCOUNTS_WRITE
  )
  out.rdcVelocity = tierFromReadWrite(
    s,
    BANKING_SCOPE.RDC_VELOCITY_READ,
    BANKING_SCOPE.RDC_VELOCITY_WRITE
  )

  return out
}

export function unionBankingCapabilities(
  a: BankingCapabilities,
  b: BankingCapabilities
): BankingCapabilities {
  return {
    customers: maxBankingPermissionTier(a.customers, b.customers),
    accounts: maxBankingPermissionTier(a.accounts, b.accounts),
    transfers: maxBankingPermissionTier(a.transfers, b.transfers),
    transactions: maxBankingPermissionTier(a.transactions, b.transactions),
    loan: maxBankingPermissionTier(a.loan, b.loan),
    rdcRequests: maxBankingPermissionTier(a.rdcRequests, b.rdcRequests),
    rdcAccounts: maxBankingPermissionTier(a.rdcAccounts, b.rdcAccounts),
    rdcVelocity: maxBankingPermissionTier(a.rdcVelocity, b.rdcVelocity)
  }
}

export function unionBankingCapabilitiesFromActiveOAuthSecrets(
  secrets: { grantedScopes: string[]; isActive?: boolean }[] | undefined
): BankingCapabilities {
  let acc = { ...DEFAULT_BANKING_CAPABILITIES }
  if (!secrets?.length) {
    return acc
  }
  for (const sec of secrets) {
    if (sec.isActive === false) {
      continue
    }
    acc = unionBankingCapabilities(
      acc,
      deriveBankingCapabilitiesFromGrantedScopes(sec.grantedScopes)
    )
  }
  return acc
}
